import React, { useState, useEffect } from 'react';
import { Artist } from '../types';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config';
import ConfirmationModal from './ui/ConfirmationModal';

interface ProjectArtistAssignmentProps {
  projectId: string | null;
  currentRole: string | null;
  onAssignmentsChange?: () => void;
}

interface ProjectArtist {
  id: string;
  name: string;
  specialization: string;
  avatarUrl?: string;
  role?: string;
  assignedAt: string;
}

const ProjectArtistAssignment: React.FC<ProjectArtistAssignmentProps> = ({
  projectId,
  currentRole,
  onAssignmentsChange
}) => {
  const { t } = useTranslation();
  const [assignedArtists, setAssignedArtists] = useState<ProjectArtist[]>([]);
  const [availableArtists, setAvailableArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Remove artist confirmation modal
  const [removeArtistModal, setRemoveArtistModal] = useState<{ isOpen: boolean; artistId: string; artistName: string }>({ isOpen: false, artistId: '', artistName: '' });

  // Permissions
  const canEdit = currentRole && ['Super Admin', 'Admin', 'Producer', 'Project Manager', 'Coordinator'].includes(currentRole);

  // Fetch assigned artists for this project
  const fetchAssignedArtists = async () => {
    if (!projectId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(API_CONFIG.PROJECTS.ASSIGN_ARTISTS(projectId), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAssignedArtists(data.artists || data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching assigned artists:', error);
    }
  };

  // Fetch all available artists
  const fetchAvailableArtists = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(API_CONFIG.ARTISTS.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableArtists(data.artists || data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching available artists:', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchAssignedArtists();
      fetchAvailableArtists();
    }
  }, [projectId]);

  // Filter available artists (exclude already assigned)
  const filteredAvailableArtists = availableArtists.filter(artist => {
    const isAlreadyAssigned = assignedArtists.some(a => a.id === artist.id);
    const matchesSearch = searchQuery === '' || 
      artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      artist.specialization.toLowerCase().includes(searchQuery.toLowerCase());
    return !isAlreadyAssigned && matchesSearch;
  });

  // Assign artist to project
  const handleAssignArtist = async (artistId: string) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(API_CONFIG.PROJECTS.ASSIGN_ARTISTS(projectId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ artistIds: [artistId] }),
      });

      if (response.ok) {
        await fetchAssignedArtists();
        setShowAddDialog(false);
        setSearchQuery('');
        if (onAssignmentsChange) onAssignmentsChange();
      }
    } catch (error) {
      console.error('❌ Error assigning artist:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remove artist from project - open confirmation modal
  const handleRemoveArtist = (artistId: string, artistName: string) => {
    if (!projectId) return;
    setRemoveArtistModal({ isOpen: true, artistId, artistName });
  };
  
  // Confirm remove artist
  const confirmRemoveArtist = async () => {
    const { artistId } = removeArtistModal;
    setRemoveArtistModal({ isOpen: false, artistId: '', artistName: '' });

    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.PROJECTS.UPDATE(projectId!)}/artists/${artistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAssignedArtists();
        if (onAssignmentsChange) onAssignmentsChange();
      }
    } catch (error) {
      console.error('❌ Error removing artist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!projectId) {
    return (
      <div className="text-center py-8 text-gray-400">
        <ICONS.User className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t('projects.saveProjectFirst') || 'Сначала сохраните проект'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300">
          {t('projects.assignedArtists') || 'Назначенные художники'} ({assignedArtists.length})
        </h4>
        {canEdit && (
          <button
            type="button"
            onClick={() => setShowAddDialog(!showAddDialog)}
            className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded transition-colors flex items-center justify-center"
            title={t('common.add') || 'Добавить'}
          >
            <ICONS.Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Add Artist Dialog */}
      {showAddDialog && canEdit && (
        <div className="bg-gray-700/50 rounded-lg p-3 space-y-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('projects.searchArtists') || 'Поиск художников...'}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          />
          <div className="max-h-48 overflow-y-auto space-y-1">
            {filteredAvailableArtists.length > 0 ? (
              filteredAvailableArtists.map(artist => (
                <button
                  key={artist.id}
                  type="button"
                  onClick={() => handleAssignArtist(artist.id)}
                  disabled={loading}
                  className="w-full flex items-center space-x-2 px-2 py-2 hover:bg-gray-600 rounded text-left transition-colors disabled:opacity-50"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-xs font-bold text-white">
                    {(artist.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{artist.name || 'Неизвестно'}</div>
                    <div className="text-xs text-gray-400 truncate">{artist.specialization || ''}</div>
                  </div>
                  <ICONS.Plus className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                </button>
              ))
            ) : (
              <p className="text-xs text-gray-400 text-center py-2">
                {t('projects.noAvailableArtists') || 'Нет доступных художников'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Assigned Artists List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {assignedArtists.length > 0 ? (
          assignedArtists.map(artist => (
            <div
              key={artist.id}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-700/50 rounded hover:bg-gray-700 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-xs font-bold text-white">
                {(artist.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{artist.name || 'Неизвестно'}</div>
                <div className="text-xs text-gray-400 truncate">{artist.specialization || ''}</div>
                {artist.role && (
                  <div className="text-xs text-indigo-400 truncate">{artist.role}</div>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleRemoveArtist(artist.id, artist.name)}
                  disabled={loading}
                  className="text-red-400 hover:text-red-300 p-1 transition-colors disabled:opacity-50"
                  title={t('common.delete') || 'Удалить'}
                >
                  <ICONS.Trash className="w-4 h-4" />
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-gray-400">
            <ICONS.User className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('projects.noAssignedArtists') || 'Нет назначенных художников'}</p>
          </div>
        )}
      </div>
      
      {/* Remove Artist Confirmation Modal */}
      <ConfirmationModal
        isOpen={removeArtistModal.isOpen}
        onClose={() => setRemoveArtistModal({ isOpen: false, artistId: '', artistName: '' })}
        onConfirm={confirmRemoveArtist}
        title={t('projects.removeArtistTitle') || 'Удалить художника'}
        message={`${t('projects.confirmRemoveArtist') || 'Удалить художника из проекта?'} "${removeArtistModal.artistName}"`}
        confirmText={t('common.delete') || 'Удалить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="danger"
      />
    </div>
  );
};

export default ProjectArtistAssignment;


